import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { MessageEventActions } from "models/constants/events/chat-event-actions";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import React from "react";
import { ConversationStateStatus } from "utilities/atoms/conversations/current-conversation/conversation-atom.reducer";
import { t } from "utilities/localization-utils";
import ChatMessageAdditionalFeedback from "./chat-message-additional-feedback";
import { FeedbackRating } from "models/enumerations/messages/feedback-rating";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import { FeedbackCategory } from "models/enumerations/messages/feedback-category";

interface ChatMessageAssistantActionsProps {
    additionalFeedbackRef: React.RefObject<HTMLDivElement>;
    conversationStatus: ConversationStateStatus;
    feedbackCategory: FeedbackCategory | undefined;
    handleCloseAdditionalFeedback: () => void;
    handleCopyClick: () => void;
    handleThumbsUpClick: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void;
    handleThumbsDownClick: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void;
    handleRetry: (action: "MessageRetried" | "MessageRegenerated") => void;
    message: ConversationMessageRecord;
    setFeedbackCategory: React.Dispatch<
        React.SetStateAction<FeedbackCategory | undefined>
    >;
    setShowAdditionalFeedback: React.Dispatch<React.SetStateAction<boolean>>;
    showAdditionalFeedback: boolean;
    thumbsUpClicked: boolean;
    thumbsDownClicked: boolean;
}

const BASE_CLASS = "c-chat-modal-message";

const ChatMessageAssistantActions: React.FC<
    ChatMessageAssistantActionsProps
> = (props) => {
    const {
        additionalFeedbackRef,
        conversationStatus,
        feedbackCategory,
        handleCopyClick,
        handleCloseAdditionalFeedback,
        handleThumbsUpClick,
        handleThumbsDownClick,
        handleRetry,
        message,
        setFeedbackCategory,
        setShowAdditionalFeedback,
        showAdditionalFeedback,
        thumbsUpClicked,
        thumbsDownClicked,
    } = props;

    return (
        <>
            <div className={`${BASE_CLASS}__copy`}>
                <Tooltip
                    content={t("copy")}
                    hideOnClick={true}
                    durationInMs={0}
                    placement={TooltipPlacement.Bottom}>
                    <Button
                        onClick={handleCopyClick}
                        style={ButtonStyles.Tertiary}
                        size={ButtonSizes.XSmall}>
                        <Icon
                            type={Icons.Copy2}
                            cssClassName={"-message-icons"}
                        />
                    </Button>
                </Tooltip>
                <Tooltip
                    content={t("thumbs-up")}
                    hideOnClick={true}
                    durationInMs={0}
                    placement={TooltipPlacement.Bottom}>
                    <Button
                        onClick={handleThumbsUpClick}
                        style={ButtonStyles.Tertiary}
                        size={ButtonSizes.XSmall}>
                        <Icon
                            type={
                                thumbsUpClicked
                                    ? Icons.ThumbsUpFilled
                                    : Icons.ThumbsUp
                            }
                            cssClassName={"-message-icons"}
                        />
                    </Button>
                </Tooltip>
                <Tooltip
                    content={t("thumbs-down")}
                    hideOnClick={true}
                    durationInMs={0}
                    placement={TooltipPlacement.Bottom}>
                    <Button
                        onClick={handleThumbsDownClick}
                        style={ButtonStyles.Tertiary}
                        size={ButtonSizes.XSmall}>
                        <Icon
                            type={
                                thumbsDownClicked
                                    ? Icons.ThumbsDownFilled
                                    : Icons.ThumbsDown
                            }
                            cssClassName={"-message-icons"}
                        />
                    </Button>
                </Tooltip>
                <Tooltip
                    content={t("retry")}
                    hideOnClick={true}
                    durationInMs={0}
                    placement={TooltipPlacement.Bottom}>
                    <Button
                        onClick={() =>
                            handleRetry(MessageEventActions.MessageRegenerated)
                        }
                        style={ButtonStyles.Tertiary}
                        size={ButtonSizes.XSmall}>
                        <Icon
                            type={Icons.Retry}
                            cssClassName={"-message-icons"}
                        />
                    </Button>
                </Tooltip>
            </div>
            {showAdditionalFeedback &&
                message.id &&
                conversationStatus ===
                    ConversationStateStatus.ConversationReady && (
                    <div
                        ref={additionalFeedbackRef}
                        key={`feedback-${message.id}`}>
                        <ChatMessageAdditionalFeedback
                            message={message}
                            feedbackRating={
                                thumbsUpClicked
                                    ? FeedbackRating.ThumbsUp
                                    : FeedbackRating.ThumbsDown
                            }
                            handleClose={handleCloseAdditionalFeedback}
                            feedbackCategory={feedbackCategory}
                            handleFeedbackCategory={setFeedbackCategory}
                            handleShowAdditionalFeedback={
                                setShowAdditionalFeedback
                            }
                        />
                    </div>
                )}
        </>
    );
};

export default ChatMessageAssistantActions;
